import { tx } from "../lib/miniI18n";
import { fxHint } from "../lib/fx";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Banknote, Smartphone, Receipt, Phone, QrCode, Upload, Share2, Download, Camera, BookOpen } from "lucide-react";
import api, { formatApiError, imgUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CardSkeletonList, EmptyState } from "../components/Common";
import { fmt, dateTimeLabel, statusColor } from "../lib/format";
import { toast } from "sonner";
import ProofImage from "../components/ProofImage";
import { SavedMethods, useSavedMethods } from "../components/SavedMethods";
import { WithdrawalEta } from "../components/WithdrawalEta";
import { downloadReceiptCard } from "../lib/receiptCard";
import { ReceiptPreview } from "../components/ReceiptPreview";

export default function Withdrawal() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [preview, setPreview] = useState(null);
  const { user, wallet, refreshWallet } = useAuth();
  const [config, setConfig] = useState(null);
  const [eta, setEta] = useState(null);
  useEffect(() => { api.get("/withdrawal/eta").then((r) => setEta(r.data)).catch(() => {}); }, []);
  const etaText = (m0) => { const m = Math.max(1, m0); return (m < 60 ? `${m} min` : m < 24 * 60 ? `${Math.round(m / 60 * 10) / 10} hr` : `${Math.round(m / 60 / 24 * 10) / 10} days`); };
  const withdrawable = config?.withdraw_mode === "total" ? (wallet?.total || 0) : (wallet?.withdrawable_balance || 0);
  const [amount, setAmount] = useState(() => (params.get("amount") || "").replace(/\D/g, ""));  // prefilled from the "You won" prompt
  const [method, setMethod] = useState("gpay");
  const [passbook, setPassbook] = useState(null);
  const [pbBusy, setPbBusy] = useState(false);
  const pbRef = useRef();
  const [upiId, setUpiId] = useState("");
  const [gpay, setGpay] = useState("");
  const [qrPath, setQrPath] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [qrInfo, setQrInfo] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const saved = useSavedMethods();
  const [accName, setAccName] = useState("");
  const [accNo, setAccNo] = useState("");
  const [ifsc, setIfsc] = useState("");
  useEffect(() => {
    if (user?.upi_id) setUpiId((v) => v || user.upi_id);
    if (user?.gpay_number) setGpay((v) => v || user.gpay_number);
    if (user?.bank_account) { setAccName((v) => v || user.bank_account.account_name || ""); setAccNo((v) => v || user.bank_account.account_number || ""); setIfsc((v) => v || user.bank_account.ifsc || ""); }
  }, [user]);
  const [submitting, setSubmitting] = useState(false);
  const [records, setRecords] = useState([]);
  const [loadingRec, setLoadingRec] = useState(true);

  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  const loadRecords = useCallback(async () => {
    if (!user) return;
    setLoadingRec(true);
    try { const { data } = await api.get("/withdrawal/my"); setRecords(data); } catch {}
    setLoadingRec(false);
  }, [user]);

  useEffect(() => { loadRecords(); }, [loadRecords]);
  useEffect(() => {
    const rid = params.get("receipt");
    if (!rid || !records.length) return;
    const r = records.find((x) => x.id === rid && x.status === "success");
    if (r) setPreview(r);
  }, [params, records]);
  const closePreview = () => { setPreview(null); if (params.get("receipt")) { params.delete("receipt"); setParams(params, { replace: true }); } };

  const pickSaved = (m, silent = false) => {
    setSelectedId(m.id); setMethod(m.method);
    if (m.method === "upi") setUpiId(m.upi_id || "");
    if (m.method === "gpay") { setGpay(m.gpay_number || ""); setAccName(m.account_name || ""); }
    if (m.method === "bank") { setAccName(m.account_name || ""); setAccNo(m.account_number || ""); setIfsc(m.ifsc || ""); }
    if (m.method === "qr") { setQrPath(m.qr_path); setQrPreview(null); setQrInfo({ upi_id: m.qr_upi_id, decoded: !!m.qr_upi_id }); }
    if (!silent) toast.success(`Using ${m.label}`);
  };
  const defaultApplied = useRef(false);
  useEffect(() => {
    if (defaultApplied.current || !saved.defaultMethod) return;
    defaultApplied.current = true; pickSaved(saved.defaultMethod, true);
  }, [saved.defaultMethod]); // eslint-disable-line

  useEffect(() => { if (user === false) navigate("/auth"); }, [user, navigate]);
  if (!user) return null;

  const submit = async () => {
    const amt = parseFloat(amount);
    const min = config?.min_withdrawal ?? 100;
    if (!amt || amt < min) return toast.error(`Minimum withdrawal is ${fmt(min)}`);
    if (amt > withdrawable) return toast.error(config?.withdraw_mode === "total" ? "Amount exceeds your balance" : "Amount exceeds withdrawable winnings");
    if (method === "upi" && !upiId.trim()) return toast.error("Enter your UPI ID");
    if (method === "bank" && (!accNo.trim() || !ifsc.trim())) return toast.error("Enter bank account number and IFSC");
    if (method === "gpay" && !/^\d{10}$/.test(gpay.trim())) return toast.error("Enter your 10-digit GPay / PhonePe number");
    if (method === "qr" && !qrPath) return toast.error("Upload your payment QR code image");
    setSubmitting(true);
    try {
      await api.post("/withdrawal", {
        amount: amt, method,
        upi_id: method === "upi" ? upiId.trim() : null,
        account_name: accName.trim() || null,
        account_number: method === "bank" ? accNo.trim() || null : null, ifsc: method === "bank" ? ifsc.trim() || null : null,
        gpay_number: method === "gpay" ? gpay.trim() : null,
        qr_path: method === "qr" ? qrPath : null,
      });
      toast.success("Withdrawal request submitted!");
      setAmount(""); setQrPath(null); setQrPreview(null); setQrInfo(null); setSelectedId(null);
      refreshWallet(); loadRecords(); saved.reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };


  const uploadQr = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setQrPath(data.path); setQrPreview(URL.createObjectURL(file)); setQrInfo(data); setSelectedId(null);
      toast.success(data.upi_id ? `QR read ✓ ${data.upi_id}` : "QR uploaded (UPI ID could not be read — admin will scan it)");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const input = "w-full rl-card px-4 py-3.5 text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30";
  const METHODS = [["gpay", "GPay / Mobile", Phone], ["qr", "QR Code", QrCode], ["bank", "Bank A/c", Banknote], ["upi", "UPI ID", Smartphone]];
  const uploadPassbook = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; e.target.value = "";
    setPbBusy(true); setPassbook(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/passbook", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPassbook(data);
      if (data.account_name) setAccName(data.account_name);
      if (data.account_number) setAccNo(data.account_number);
      if (data.ifsc) setIfsc(data.ifsc);
      toast[data.account_number ? "success" : "error"](data.account_number ? "Passbook read ✓ — please verify the details below" : "Could not read the passbook clearly. Try a sharper photo of the first page.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setPbBusy(false); }
  };

  return (
    <div className="space-y-4 pb-6">
      <button onClick={() => navigate("/me")} className="flex items-center gap-1 text-slate-500 text-sm"><ArrowLeft className="w-4 h-4" />{tx("Back")}</button>

      <div className="rl-card p-4">
        <div className="text-xs text-slate-500">{tx("Balance")} <b className="font-mono text-slate-700">{fmt(wallet?.total)}</b>{config?.withdraw_mode !== "total" && <span className="ml-1" data-testid="withdraw-play-balance">= {tx("winnings")} <b className="font-mono text-emerald-600">{fmt(wallet?.withdrawable_balance)}</b> + {tx("recharge (play only)")} <b className="font-mono text-slate-600">{fmt(wallet?.cash_balance)}</b></span>}</div>
        <div className="text-xs text-slate-500 mt-1">{config?.withdraw_mode === "total" ? tx("Withdrawable") : tx("Withdrawable (winnings)")}</div>
        <div className="font-mono font-black text-emerald-400 text-2xl" data-testid="withdrawable-amount">{fmt(withdrawable)}{fxHint(withdrawable) && <span className="ml-2 text-xs font-semibold text-emerald-200/80" data-testid="fx-hint">{fxHint(withdrawable)}</span>}</div>
        <div className="text-[10px] text-slate-500 mt-1">{config?.withdraw_mode === "total" ? "Your whole balance can be withdrawn." : tx("Recharged money is for play — only winnings can be withdrawn.")} Min {fmt(config?.min_withdrawal ?? 100)}. <span className="text-emerald-700 font-semibold" data-testid="withdraw-eta-promise">{eta?.median_minutes != null ? `${tx("Usually paid in")} ~${etaText(eta.median_minutes)} (${tx("last")} ${eta.since_days} ${tx("days")}, ${eta.sample} ${tx("payouts")})` : `Paid within ${config?.withdrawal_eta_text || "30 min – 1 hr 30 min"}.`}</span></div>
      </div>

      <div>
        <label className="text-xs text-slate-500">{tx("Withdrawal Amount")}</label>
        <input inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} placeholder={tx("₹0")}
          className={input + " mt-1 font-mono text-xl"} data-testid="withdraw-amount-input" />
        <div className="flex gap-2 mt-2 flex-wrap" data-testid="withdraw-quick-amounts">
          {[500, 1000, 2000].map((v) => (
            <button key={v} type="button" onClick={() => setAmount(String(v))} disabled={v > Math.floor(withdrawable)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-transform active:scale-95 disabled:opacity-40 ${String(v) === amount ? "bg-emerald-500 text-white border-emerald-500" : "bg-slate-100 text-slate-700 border-slate-200"}`} data-testid={`withdraw-quick-${v}`}>₹{v.toLocaleString("en-IN")}</button>
          ))}
          <button type="button" onClick={() => setAmount(String(Math.floor(withdrawable)))} disabled={Math.floor(withdrawable) < 1}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-transform active:scale-95 disabled:opacity-40 ${amount && Number(amount) === Math.floor(withdrawable) ? "bg-emerald-500 text-white border-emerald-500" : "bg-amber-100 text-amber-800 border-amber-300"}`} data-testid="withdraw-quick-all">{tx("All")} · {fmt(Math.floor(withdrawable))}</button>
        </div>
      </div>

      <SavedMethods methods={saved.methods} selectedId={selectedId} onPick={pickSaved} />

      <div>
        <label className="text-xs text-slate-500">{tx("Receive money via")}</label>
        <div className="grid grid-cols-4 gap-2 mt-1" data-testid="withdraw-method-row">
          {METHODS.map(([m, l, Icon]) => (
            <button key={m} onClick={() => setMethod(m)} data-testid={`withdraw-method-${m}`} className={`py-2.5 rounded-xl flex flex-col items-center justify-center gap-1 text-[11px] font-semibold ${method === m ? "rl-gold-bg" : "rl-card text-slate-600"}`}><Icon className="w-4 h-4" />{l}</button>
          ))}
        </div>
      </div>

      {method === "upi" && <input value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder={tx("yourname@upi")} className={input} data-testid="withdraw-upi-input" />}
      {method === "bank" && (
        <div className="space-y-2">
          <input ref={pbRef} type="file" accept="image/*" className="hidden" onChange={uploadPassbook} data-testid="withdraw-passbook-file" />
          <button onClick={() => pbRef.current?.click()} disabled={pbBusy} className="w-full rl-card border-dashed border-slate-300 py-3 rounded-2xl flex items-center justify-center gap-2 text-sm font-semibold text-slate-700" data-testid="withdraw-passbook-btn">
            {pbBusy ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> : <BookOpen className="w-4 h-4 text-blue-600" />}{pbBusy ? tx("Reading passbook…") : tx("Upload passbook photo — auto-fill account details")}
          </button>
          {passbook && (
            <div className="rounded-xl bg-blue-50 border border-blue-200 p-2.5 text-[11px] text-slate-700 space-y-0.5" data-testid="withdraw-passbook-result">
              <div className="font-bold text-blue-800">{tx("Read from passbook — please verify")}</div>
              <div>Holder: <b data-testid="passbook-holder">{passbook.account_name || "—"}</b></div>
              <div>Account No: <b className="font-mono" data-testid="passbook-accno">{passbook.account_number || "—"}</b></div>
              <div>IFSC: <b className="font-mono" data-testid="passbook-ifsc">{passbook.ifsc || "—"}</b></div>
              <div>Bank: <b data-testid="passbook-bank">{passbook.bank_name || "—"}</b>{passbook.branch ? <> · Branch: <b>{passbook.branch}</b></> : null}</div>
            </div>
          )}
          <input value={accName} onChange={(e) => setAccName(e.target.value)} placeholder={tx("Account Holder Name")} className={input} data-testid="withdraw-accname-input" />
          <input value={accNo} onChange={(e) => setAccNo(e.target.value)} placeholder={tx("Account Number")} inputMode="numeric" className={input} data-testid="withdraw-accno-input" />
          <input value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} placeholder={tx("IFSC Code")} className={input} data-testid="withdraw-ifsc-input" />
        </div>
      )}
      {method === "gpay" && (
        <div className="space-y-2">
          <input value={gpay} onChange={(e) => setGpay(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder={tx("GPay / PhonePe mobile number")} inputMode="numeric" className={input + " font-mono"} data-testid="withdraw-gpay-input" />
          <input value={accName} onChange={(e) => setAccName(e.target.value)} placeholder={tx("Name on GPay (optional)")} className={input} data-testid="withdraw-gpay-name-input" />
          <p className="text-[10px] text-slate-500">{tx("We will send money to this number on Google Pay / PhonePe / Paytm.")}</p>
        </div>
      )}
      {method === "qr" && (
        <div className="rl-card p-3 space-y-2" data-testid="withdraw-qr-box">
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-slate-300 rounded-xl py-3 cursor-pointer text-xs font-semibold text-slate-600" data-testid="withdraw-qr-camera-label">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}{tx("Take photo")}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadQr(e.target.files?.[0])} data-testid="withdraw-qr-camera" />
            </label>
            <label className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-slate-300 rounded-xl py-3 cursor-pointer text-xs font-semibold text-slate-600">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{qrPath ? tx("Change QR image") : tx("From gallery")}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadQr(e.target.files?.[0])} data-testid="withdraw-qr-file" />
            </label>
          </div>
          {qrPreview ? <img src={qrPreview} alt="QR preview" className="w-32 h-32 object-contain mx-auto rounded-lg border bg-white" data-testid="withdraw-qr-preview" /> : qrPath ? <div className="w-32 mx-auto" data-testid="withdraw-qr-saved-preview"><ProofImage path={qrPath} /></div> : null}
          {qrPath && qrInfo && (qrInfo.upi_id
            ? <div className="text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-lg px-2 py-1 text-center font-mono" data-testid="withdraw-qr-upi">✓ UPI ID read: {qrInfo.upi_id}{qrInfo.name ? ` · ${qrInfo.name}` : ""}</div>
            : <div className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-2 py-1 text-center" data-testid="withdraw-qr-unread">{tx("UPI ID could not be read — our team will scan the image to pay you.")}</div>)}
          <p className="text-[10px] text-slate-500 text-center">{tx("Open GPay / PhonePe → your QR code → screenshot → upload here. We scan it and pay you.")}</p>
        </div>
      )}

      <button onClick={submit} disabled={submitting} className="w-full rl-gold-bg font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2" data-testid="withdraw-submit-btn">
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}Submit Withdrawal Request
      </button>

      <h2 className="font-display font-bold text-slate-900 pt-2">{tx("Withdrawal History")}</h2>
      {loadingRec ? <CardSkeletonList count={2} /> : records.length === 0 ? (
        <EmptyState icon={Banknote} title="No withdrawals yet" subtitle="Your requests will appear here." />
      ) : (
        <div className="space-y-2">
          {records.map((r) => (
            <div key={r.id} className="rl-card p-3.5" data-testid={`withdraw-record-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono font-bold text-slate-900">{fmt(r.amount)}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${statusColor(r.status)}`}>{r.status}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{r.method.toUpperCase()} · {r.destination}</div>
              <div className="text-[10px] text-slate-500">{dateTimeLabel(r.created_at)}</div>
              <WithdrawalEta record={r} etaHours={config?.withdrawal_eta_hours || 2} />
              {r.status === "success" && (
                <div className="mt-2 flex gap-2" data-testid={`withdraw-receipt-actions-${r.id}`}>
                  <button onClick={() => setPreview(r)} className="flex-1 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold flex items-center justify-center gap-1.5" data-testid={`withdraw-receipt-share-${r.id}`}><Share2 className="w-3.5 h-3.5" />{tx("View & Share Receipt")}</button>
                  <button onClick={() => downloadReceiptCard(r)} className="w-10 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center" aria-label="download receipt" data-testid={`withdraw-receipt-download-${r.id}`}><Download className="w-4 h-4" /></button>
                </div>
              )}
              {r.status === "success" && (r.receipt_url || r.receipt_note) && (
                <div className="mt-2 rounded-xl bg-emerald-50 border border-emerald-100 p-2.5 flex items-center gap-3" data-testid={`withdraw-receipt-${r.id}`}>
                  {r.receipt_url && (
                    <a href={imgUrl(r.receipt_url)} target="_blank" rel="noreferrer" className="shrink-0">
                      <img src={imgUrl(r.receipt_url)} alt="payment receipt" className="w-12 h-12 rounded-lg object-cover border" />
                    </a>
                  )}
                  <div className="min-w-0">
                    <div className="text-[11px] font-bold text-emerald-700 flex items-center gap-1"><Receipt className="w-3.5 h-3.5" />{tx("Payment Receipt")}</div>
                    {r.receipt_note && <div className="text-[11px] text-slate-600 break-words">Ref: {r.receipt_note}</div>}
                    {r.receipt_url && <div className="text-[10px] text-emerald-600">{tx("Tap image to view full receipt")}</div>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {preview && <ReceiptPreview record={preview} onClose={closePreview} />}
    </div>
  );
}
